import { useEffect, useRef } from 'react';
import { parsePageParam } from '../lib/filters';

type UsePageUrlSyncArgs = {
  currentPage: number | undefined;
  pageChanged: (page: number) => void;
  searchParams: URLSearchParams;
};

/**
 * Applies the `page` query param onto the list's page state when the URL's
 * page itself changes — a direct URL edit or browser back/forward. Missing or
 * invalid values fall back to page 1.
 *
 * It compares against the URL page it last saw, not against mount: StrictMode
 * runs mount effects twice, and the mount-time read belongs to each list,
 * alongside the rest of its filters.
 */
export function usePageUrlSync({
  currentPage,
  pageChanged,
  searchParams,
}: UsePageUrlSyncArgs) {
  const currentPageRef = useRef(currentPage);
  useEffect(() => {
    currentPageRef.current = currentPage;
  });

  const lastUrlPageRef = useRef(parsePageParam(searchParams.get('page')));

  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts to a URL change only
  useEffect(() => {
    const urlPage = parsePageParam(searchParams.get('page'));

    if (urlPage === lastUrlPageRef.current) {
      return;
    }
    lastUrlPageRef.current = urlPage;

    if (urlPage !== (currentPageRef.current || 1)) {
      pageChanged(urlPage);
    }
  }, [searchParams]);
}
